import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { AuthContext } from 'src/modules/user/context'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import {
  clearPendingConnectedAppAuthorize,
  savePendingConnectedAppAuthorize,
  type PendingConnectedAppAuthorize,
} from 'src/modules/user/requirements/pending-connected-app.storage'
import { ApiService } from 'src/services/api.service'

import classes from './connected-app-authorize-page.module.scss'

const maxStateLength = 40

/** Delay before automatic redirect after a successful approve (ms). */
const REDIRECT_DELAY_MS = 3000

const successTitleId = 'connected-app-authorize-success-title'
const successSubtitleId = 'connected-app-authorize-success-subtitle'

const successTitleText = 'Authorization is complete'

function getConnectedAppPlatformLabel(redirectUrl: string): string | null {
  try {
    const host = new URL(redirectUrl).hostname.toLowerCase()
    if (host.includes('shopify') || host.endsWith('.myshopify.com')) {
      return 'Shopify'
    }
    if (host.includes('woocommerce')) {
      return 'WooCommerce'
    }
    if (host.includes('wordpress') || host.endsWith('.wordpress.com') || /(^|\.)wp\./.test(host)) {
      return 'WordPress'
    }
    return null
  } catch {
    return null
  }
}

export function ConnectedAppAuthorize() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated } = useContext(AuthContext)
  const [error, setError] = useState<string | null>(null)
  const [hasStarted, setHasStarted] = useState(false)
  const [successRedirectUrl, setSuccessRedirectUrl] = useState<string | null>(null)

  const statusHeadingRef = useRef<HTMLElement>(null)
  const redirectCommittedRef = useRef(false)
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const profile = useProfile({}, { enabled: Boolean(isAuthenticated) })
  const bookingOnboarding = useBookingOnboarding({
    enabled: Boolean(isAuthenticated && profile.data?.is_booking_user),
  })

  const canApproveConnectedApp = useMemo(() => {
    if (!profile.data) {
      return false
    }
    if (profile.data.is_booking_user) {
      if (bookingOnboarding.isLoading) {
        return false
      }
      return bookingOnboarding.data?.onboarding_completed === true
    }
    return profile.data.completed_onboarding === true
  }, [profile.data, bookingOnboarding.data?.onboarding_completed, bookingOnboarding.isLoading])

  const pendingAuthorize = useMemo<PendingConnectedAppAuthorize | null>(() => {
    const params = new URLSearchParams(location.search)
    const state = params.get('state')?.trim()

    if (!state || state.length > maxStateLength) {
      return null
    }

    return {
      state,
      isBookingUser: params.get('isBookingUser') === 'true',
      source: params.get('source')?.trim() || undefined,
      createdAt: new Date().toISOString(),
    }
  }, [location.search])

  const platformLabel = successRedirectUrl ? getConnectedAppPlatformLabel(successRedirectUrl) : null
  const successSubtitleText = platformLabel
    ? `We'll take you back to ${platformLabel} in a second.`
    : `We'll take you back to your connected app in a second.`

  const finalizeRedirect = useCallback((url: string) => {
    if (redirectCommittedRef.current) {
      return
    }
    redirectCommittedRef.current = true
    if (redirectTimerRef.current !== null) {
      clearTimeout(redirectTimerRef.current)
      redirectTimerRef.current = null
    }
    clearPendingConnectedAppAuthorize()
    window.location.assign(url)
  }, [])

  useEffect(() => {
    if (!pendingAuthorize) {
      return
    }
    savePendingConnectedAppAuthorize(pendingAuthorize)
  }, [pendingAuthorize])

  useEffect(() => {
    if (!pendingAuthorize || isAuthenticated) {
      return
    }
    navigate(`${routeNames.login.path}${location.search}`, { replace: true })
  }, [isAuthenticated, location.search, navigate, pendingAuthorize])

  useEffect(() => {
    if (!pendingAuthorize || !isAuthenticated || !profile.data) {
      return
    }
    if (profile.data.is_booking_user || profile.data.completed_onboarding) {
      return
    }
    navigate(routeNames.configuration.path, { replace: true })
  }, [isAuthenticated, navigate, pendingAuthorize, profile.data])

  useEffect(() => {
    if (!pendingAuthorize || !isAuthenticated || hasStarted) {
      return
    }
    if (!canApproveConnectedApp) {
      return
    }
    setHasStarted(true)
    ApiService.ConnectedApps.approveAuthorize(pendingAuthorize.state)
      .then(({ redirectUrl }) => {
        setSuccessRedirectUrl(redirectUrl)
      })
      .catch((err) => {
        console.error(err)
        setError(
          'Unable to approve this connected app request. Please restart the connection flow from the external app.',
        )
      })
  }, [
    bookingOnboarding.data?.onboarding_completed,
    bookingOnboarding.isLoading,
    canApproveConnectedApp,
    hasStarted,
    isAuthenticated,
    pendingAuthorize,
    profile.data,
  ])

  useEffect(() => {
    if (!successRedirectUrl) {
      return
    }
    redirectTimerRef.current = setTimeout(() => {
      finalizeRedirect(successRedirectUrl)
    }, REDIRECT_DELAY_MS)
    return () => {
      if (redirectTimerRef.current !== null) {
        clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
    }
  }, [finalizeRedirect, successRedirectUrl])

  useEffect(() => {
    if (!successRedirectUrl) {
      return
    }
    statusHeadingRef.current?.focus()
  }, [successRedirectUrl])

  if (!pendingAuthorize) {
    return <Typography variant="body">Invalid or missing connected app authorization state.</Typography>
  }

  if (error) {
    return <Typography variant="body">{error}</Typography>
  }

  if (successRedirectUrl) {
    return (
      <div className={classes.overlay} role="presentation">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={successTitleId}
          aria-describedby={successSubtitleId}
          className={classes.panel}
        >
          <div role="status" aria-live="polite" aria-atomic="true">
            <Typography
              ref={statusHeadingRef}
              id={successTitleId}
              variant="h4"
              element="h2"
              className={classes.title}
              tabIndex={-1}
            >
              {successTitleText}
            </Typography>
            <Typography id={successSubtitleId} variant="body" element="p" className={classes.subtitle}>
              {successSubtitleText}
            </Typography>
          </div>
          <Typography variant="body" className={classes.fallback}>
            <button
              type="button"
              className={classes.fallbackButton}
              onClick={() => finalizeRedirect(successRedirectUrl)}
            >
              If that doesn&apos;t work, click here
            </button>
          </Typography>
        </div>
      </div>
    )
  }

  return <Loader isLoading full />
}
